import {
    CdkConnectedOverlay,
    ConnectedPosition,
    FlexibleConnectedPositionStrategyOrigin,
    OverlayModule,
} from '@angular/cdk/overlay';
import { CommonModule, Location } from '@angular/common';
import {
    afterNextRender,
    Component,
    DestroyRef,
    computed,
    effect,
    inject,
    Injector,
    OnInit,
    signal,
    untracked,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
    ClientEntity,
    DocumentController,
    DocumentEntity,
    DocumentStatusEnum,
    DocumentTypeEnum,
    FeatureFlagController,
    OrderController,
    OrderEntity,
    TemplateEntity,
    UserController,
    batchHasZip,
    DocumentBatchEntity,
} from '@domain';
import {
    RaButtonComponent,
    RaLoadingComponent,
    RaToastService,
} from '@shared/components';
import { DocumentCreateController } from 'src/app/domain/controllers/document/document-create.controller';
import { InternalTemplateEntity } from 'src/app/domain/entities/internal-template.entity';
import { OrderResumeStatusEnum } from 'src/app/domain/enums/order-resume-status.enum';
import { CreateDocumentOptionsComponent } from 'src/app/modules/order/components/create-document-options/create-document-options.component';
import { ImportDocumentsModalComponent } from 'src/app/modules/order/components/import-documents-modal/import-documents-modal.component';
import { ModalDocumentBatchComponent } from 'src/app/modules/order/components/modal-document-batch/modal-document-batch.component';
import { DocumentCardSkeletonComponent } from 'src/app/modules/order/components/document-card-skeleton/document-card-skeleton.component';
import { ModalMembersComponent } from 'src/app/modules/order/components/modal-members/modal-members.component';
import { RaEmptyComponent } from 'src/app/shared/components/ra-empty/ra-empty.component';
import { RaFullPageLoadingComponent } from 'src/app/shared/components/ra-full-page-loading/ra-full-page-loading.component';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { RaModalEditTextComponent } from 'src/app/shared/components/ra-modal-edit-text/ra-modal-edit-text.component';
import { RaModalListClientsComponent } from 'src/app/shared/components/ra-modal-list-clients/ra-modal-list-clients.component';
import { RaModalListInternalTemplatesComponent } from 'src/app/shared/components/ra-modal-list-internal-templates/ra-modal-list-internal-templates.component';
import { RaModalListOrdersComponent } from 'src/app/shared/components/ra-modal-list-orders/ra-modal-list-orders.component';
import { RaModalListTemplatesComponent } from 'src/app/shared/components/ra-modal-list-templates/ra-modal-list-templates.component';
import { RaBadgeComponent } from 'src/app/shared/components/ra-badge';
import { RaClickOutsideDirective, TestIdDirective } from 'src/app/shared/directives';
import {
    RaMenuComponent,
    RaMenuItem,
    RaProgressBarComponent,
    RaTooltipDirective,
} from 'ra-ui';
import { PusherService } from 'src/app/shared/utils/pusher/pusher.service';
import { ChecklistCompletionPipe } from '../../../../../../shared/pipes/documents/checklist-completion.pipe';
import { DocumentSizeGBPipe } from '../../../../../../shared/pipes/documents/document-size.pipe';
import { IconByDocumentTypePipe } from '../../../../../../shared/pipes/documents/icon-by-document-type.pipe';
import { IconColorByDocumentTypePipe } from '../../../../../../shared/pipes/documents/icon-color-by-document-type.pipe';
import { FormatFileSizePipe } from '../../../../../../shared/pipes/format-file-size.pipe';
import { importGenerationPercent } from 'src/app/modules/order/import-progress.util';
import { FeedMainService } from '../../services/feed-main.service';
import { OrderMainService } from '../../services/order-main.service';
import { I18nService } from 'src/app/shared/services/i18n.service';
import { DocumentBatchService } from 'src/app/shared/services/document-batch.service';
import { DocumentBatchController } from 'src/app/domain/controllers/document/document-batch.controller';
import { DocumentsViewEnum } from 'src/app/domain/enums/documents-view.enum';
import { groupDocumentsByMonth } from 'src/app/modules/order/documents-by-month.util';

/** Menu de um documento aberto: de qual documento e onde ele se ancora. */
interface DocumentMenuState {
    document: DocumentEntity;
    /** Elemento que abriu o menu, para devolver o foco ao fechar com Esc. */
    trigger: HTMLElement;
    /**
     * `pointer`: clique direito, abre no ponto do cursor. `button`: o ⋯ do card.
     * `keyboard`: Shift+F10 / tecla de menu, ancorado no card.
     */
    source: 'pointer' | 'button' | 'keyboard';
}

@Component({
    selector: 'order-details',
    templateUrl: 'order-details.component.html',
    styleUrl: 'order-details.component.scss',
    imports: [
        CommonModule,
        RaEmptyComponent,
        RaFullPageLoadingComponent,
        FormatFileSizePipe,
        RaTooltipDirective,
        RaLoadingComponent,
        RaBadgeComponent,
        RaButtonComponent,
        RaMenuComponent,
        RaProgressBarComponent,
        RaModalEditTextComponent,
        RaIconComponent,
        RaModalListClientsComponent,
        ModalMembersComponent,
        CreateDocumentOptionsComponent,
        ImportDocumentsModalComponent,
        ModalDocumentBatchComponent,
        DocumentCardSkeletonComponent,
        RaClickOutsideDirective,
        TestIdDirective,
        RaModalListTemplatesComponent,
        RaModalListInternalTemplatesComponent,
        RaModalListOrdersComponent,
        OverlayModule,
        IconByDocumentTypePipe,
        IconColorByDocumentTypePipe,
        ChecklistCompletionPipe,
        DocumentSizeGBPipe,
        TranslatePipe,
    ],
})
export class OrderDetailsComponent implements OnInit {
    public loadingDocuments = signal(false);
    public documents = signal<DocumentEntity[]>([]);
    public isModalListClientsVisible = signal(false);
    public isEditingDescription = signal(false);
    public loadingActions = signal(false);
    public loadingMembers = signal(false);
    public loadingActionsMessage = signal<string>('');
    public isModalMembersVisible = signal(false);
    public isCreateDocumentOptionsVisible = signal(false);

    /** Selo do código virou "copiado" por alguns segundos após o clique. */
    public codeCopied = signal(false);
    private _codeCopiedTimeout?: ReturnType<typeof setTimeout>;
    public isImportDocumentsVisible = signal(false);
    public isDocumentBatchVisible = signal(false);
    public loadingCreateDocument = signal(false);
    public isModalListTemplatesVisible = signal(false);
    public isModalListInternalTemplatesVisible = signal(false);
    public isActionsMenuVisible = signal(false);
    public isBatchMenuVisible = signal(false);

    /** Menu de opções do documento (botão ⋯ do card ou clique direito). `null` = fechado. */
    public documentMenu = signal<DocumentMenuState | null>(null);

    /**
     * Onde o menu se ancora. Fica com o último valor ao fechar: o overlay do CDK
     * ainda lê a origem enquanto se desmonta.
     */
    public documentMenuOrigin = signal<FlexibleConnectedPositionStrategyOrigin>({ x: 0, y: 0 });

    /** Documento no modal de renomear / no seletor de serviço do "Mover". */
    public documentToRename = signal<DocumentEntity | null>(null);
    public documentToMove = signal<DocumentEntity | null>(null);

    private _documentMenuOverlay = viewChild(CdkConnectedOverlay);

    /** No ponto do clique direito: abre para baixo e à direita, e vira onde não couber. */
    private readonly _pointerMenuPositions: ConnectedPosition[] = [
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'top' },
        { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top' },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
        { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
    ];

    /** No botão ⋯: alinhado pela direita, embaixo dele (em cima se não couber). */
    private readonly _buttonMenuPositions: ConnectedPosition[] = [
        { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 6 },
        { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -6 },
    ];

    public documentMenuPositions = computed(() =>
        this.documentMenu()?.source === 'pointer'
            ? this._pointerMenuPositions
            : this._buttonMenuPositions
    );

    /**
     * ZIP pronto do serviço, como o servidor o vê.
     *
     * Não dá para ler só do `DocumentBatchService`: dispensar a pílula solta o pacote de
     * lá, mas o arquivo continua valendo no servidor. É por este que o menu decide entre
     * gerar e baixar.
     */
    private _orderZip = signal<DocumentBatchEntity | null>(null);

    /** O ZIP que vale para este serviço agora, venha do acompanhamento ao vivo ou do servidor. */
    public readonly orderZipBatch = computed(() => {
        const orderId = this.orderMainService.currentOrder()?._id;
        if (!orderId) return null;

        const live = this.batchService.batch();
        if (live && live.orderId === orderId && batchHasZip(live)) return live;

        const stored = this._orderZip();
        return stored && stored.orderId === orderId && batchHasZip(stored) ? stored : null;
    });
    public orderResumeStatusEnum = OrderResumeStatusEnum;

    /** ID do serviço atual: a lista de documentos depende só dele. */
    private _currentOrderId = computed(() => this.orderMainService.currentOrder()?._id);
    public documentStatusEnum = DocumentStatusEnum;
    public documentsViewEnum = DocumentsViewEnum;

    // Placeholders exibidos enquanto os documentos carregam
    protected readonly documentSkeletons = Array.from({ length: 8 });

    /** Opções do seletor "Visualização" da lista de documentos. */
    protected readonly viewOptions = [
        { value: DocumentsViewEnum.CARDS, icon: 'grid-2', label: 'order.details.viewCards' },
        { value: DocumentsViewEnum.MONTH, icon: 'calendar', label: 'order.details.viewMonth' },
    ];

    /** Documentos agrupados por mês (visão "por mês"), do mais recente ao mais antigo. */
    public documentsByMonth = computed(() => groupDocumentsByMonth(this.documents()));

    /** Iniciais do cliente para o avatar do header. */
    public clientInitials = computed(() => {
        const name = this.orderMainService.currentOrder()?.client?.name?.trim() ?? '';
        if (!name) return '';
        const parts = name.split(' ').filter(Boolean);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return parts[0][0].toUpperCase();
    });

    // * [Injects]
    public orderMainService = inject(OrderMainService);
    private _userController = inject(UserController);

    /** Visão escolhida pelo usuário (cards ou por mês). Usuário sem o campo vê cards. */
    public documentsView = computed(
        () => this._userController.currentUser()?.documentsView ?? DocumentsViewEnum.CARDS
    );
    public batchService = inject(DocumentBatchService);
    private _batchController = inject(DocumentBatchController);
    public orderController = inject(OrderController);
    public featureFlagController = inject(FeatureFlagController);
    public documentController = inject(DocumentController);
    private _documentCreateController = inject(DocumentCreateController);
    private _destroyRef = inject(DestroyRef);
    private _toastService = inject(RaToastService);
    private _pusherService = inject(PusherService);
    private _translate = inject(TranslateService);
    private _i18n = inject(I18nService);
    private _feedMainService = inject(FeedMainService);
    private _router = inject(Router);
    private _location = inject(Location);
    private _injector = inject(Injector);
    private _processingDocumentsTimer?: ReturnType<typeof setInterval>;

    /** Itens do menu "Opções do serviço". */
    public actionsMenuItems = computed<RaMenuItem[]>(() => {
        const items: RaMenuItem[] = [];

        if (!this.featureFlagController.isBlocked('resume_order_ia')) {
            items.push({
                value: 'resume',
                label: this._i18n.t('order.details.resumeTooltip'),
                icon: 'textalign-justifyleft',
                // Um resumo por vez: pedido no ar ou ainda sendo gerado
                disabled: this.loadingActions() || this._isResumePending(),
            });
        }

        items.push(
            {
                value: 'edit-description',
                label: this._i18n.t('order.details.editDescriptionTooltip'),
                icon: 'edit',
            },
            {
                value: 'change-client',
                label: this._i18n.t('order.details.changeClientTooltip'),
                icon: 'repeat',
            },
            // Um item só para o lote: com ZIP pronto, "Baixar ZIP"; sem, "Gerar PDFs em
            // lote". Antes eram os dois, com o gerar desabilitado enquanto o ZIP valia,
            // o que só repetia a mesma informação.
            this.orderZipBatch()
                ? {
                      value: 'batch-download',
                      label: this._i18n.t('order.documentBatch.downloadZip'),
                      icon: 'document-download',
                      disabled: this.batchService.downloading(),
                      dividerBefore: true,
                  }
                : {
                      value: 'batch-pdf',
                      label: this._i18n.t('order.documentBatch.openButton'),
                      icon: 'document-download',
                      // Só com mais de um documento: para um só, o caminho é o PDF do
                      // próprio documento, que não passa pelo engine nem cobra pacote.
                      disabled: this.documents().length < 2 || this.batchService.running(),
                      dividerBefore: true,
                  },
            {
                value: 'archive',
                label: this._i18n.t('order.details.archiveLabel'),
                icon: 'archive',
                dividerBefore: true,
            },
            {
                value: 'remove',
                label: this._i18n.t('order.details.removeTooltip'),
                icon: 'trash',
                variant: 'danger',
                dividerBefore: true,
            }
        );

        return items;
    });

    /** Itens do menu de um documento (botão ⋯ do card ou clique direito). */
    public documentMenuItems = computed<RaMenuItem[]>(() => {
        // Uma ação de documento por vez: duplicar/mover ainda em andamento trava as outras
        const busy = this.loadingActions();

        return [
            {
                value: 'open',
                label: this._i18n.t('order.details.documentMenu.open'),
                icon: 'arrow-right',
            },
            {
                value: 'open-new-tab',
                label: this._i18n.t('order.details.documentMenu.openNewTab'),
                icon: 'maximize-3',
            },
            {
                value: 'rename',
                label: this._i18n.t('order.details.documentMenu.rename'),
                icon: 'edit',
                disabled: busy,
                dividerBefore: true,
            },
            {
                value: 'duplicate',
                label: this._i18n.t('order.details.documentMenu.duplicate'),
                icon: 'copy',
                disabled: busy,
            },
            {
                value: 'move',
                label: this._i18n.t('order.details.documentMenu.move'),
                icon: 'arrow-swap-horizontal',
                disabled: busy,
            },
            {
                value: 'remove',
                label: this._i18n.t('order.details.documentMenu.remove'),
                icon: 'trash',
                variant: 'danger',
                disabled: busy,
                dividerBefore: true,
            },
        ];
    });

    /** Itens do menu do pacote pronto (ícone com selo no cabeçalho). */
    public batchMenuItems = computed<RaMenuItem[]>(() => [
        {
            value: 'download',
            label: this._i18n.t('order.documentBatch.downloadZip'),
            description: this._i18n.t('order.documentBatch.menuHint', {
                count: this.batchService.succeeded(),
            }),
            icon: 'document-download',
            disabled: this.batchService.downloading(),
        },
        {
            value: 'dismiss',
            label: this._i18n.t('order.documentBatch.dismiss'),
            icon: 'close-circle',
        },
    ]);

    /** Ação do menu do pacote pronto. */
    public onBatchMenuSelect(value: string): void {
        this.isBatchMenuVisible.set(false);

        if (value === 'download') this.batchService.downloadZip();
        if (value === 'dismiss') this.batchService.dismiss();
    }

    /** Despacha a ação escolhida e fecha o menu. */
    public onActionSelect(value: string): void {
        this.isActionsMenuVisible.set(false);

        switch (value) {
            case 'resume':
                this.createResume();
                break;
            case 'edit-description':
                this.isEditingDescription.set(true);
                break;
            case 'batch-pdf':
                this.isDocumentBatchVisible.set(true);
                break;
            case 'batch-download':
                this.batchService.downloadZip(this.orderZipBatch());
                break;
            case 'change-client':
                this.isModalListClientsVisible.set(true);
                break;
            case 'archive':
                this.archiveOrder();
                break;
            case 'remove':
                this.removeOrder();
                break;
        }
    }

    constructor() {
        // Depende só do ID: editar descrição, cliente ou equipe troca o objeto do
        // serviço, e isso não pode rebuscar os documentos (piscava o skeleton).
        // Também cobre a carga inicial, então o ngOnInit não busca de novo.
        effect(() => {
            const orderId = this._currentOrderId();
            untracked(() => {
                // O menu aberto era de um documento do serviço anterior
                this.closeDocumentMenu();

                if (orderId) this._getDocuments();
                else this._stopProcessingDocumentsPolling();
            });
        });

        // Recarrega os documentos quando algo (ex.: conclusão do chat de IA) solicita.
        effect(() => {
            if (this.orderMainService.documentsReloadTick() > 0) {
                untracked(() => this._getDocuments());
            }
        });

        // Clique na ilha do rodapé: o serviço sai do minimizado e a tela é quem
        // reabre o modal, no passo em que a geração estiver.
        effect(() => {
            if (!this.batchService.minimized() && this.batchService.running()) {
                this.isDocumentBatchVisible.set(true);
            }
        });
    }

    ngOnInit(): void {
        this._adoptCurrentBatch();

        // Inicializa os pusher
        this._initializePusher(this._destroyRef);
        this._destroyRef.onDestroy(() => this._stopProcessingDocumentsPolling());
    }

    /**
     * Retoma o pacote do serviço ao abrir a tela: um F5 no meio da geração não pode
     * parecer que o lote sumiu. Entra minimizado — quem abriu a tela não pediu o modal
     * agora, pediu antes.
     */
    private _adoptCurrentBatch(): void {
        const orderId = this.orderMainService.currentOrder()?._id;
        if (!orderId) return;

        this._batchController.getCurrentByOrder(orderId).subscribe({
            next: batch => {
                this._orderZip.set(batchHasZip(batch) ? batch : null);
                if (!batch) return;

                this.batchService.adopt(batch);
                if (this.batchService.running()) this.batchService.minimize();
            },
        });
    }

    /**
     * Abre o menu de opções conferindo o ZIP no servidor.
     *
     * Um documento editado, criado ou apagado depois que a tela abriu aposenta o pacote
     * lá, e o menu não pode continuar travando "Gerar PDFs em lote" por causa dele.
     */
    public openActionsMenu(): void {
        this.isActionsMenuVisible.set(true);

        const orderId = this.orderMainService.currentOrder()?._id;
        if (!orderId) return;

        this._batchController.getCurrentByOrder(orderId).subscribe({
            next: batch => {
                const zip = batchHasZip(batch) ? batch : null;
                this._orderZip.set(zip);

                // O pacote ao vivo da aba também saiu de cena no servidor.
                const live = this.batchService.batch();
                if (live && live.orderId === orderId && batchHasZip(live) && live._id !== zip?._id) {
                    this.batchService.recheckOrderPackage();
                }
            },
        });
    }

    private _getDocuments(showLoading = true): void {
        const orderId = this.orderMainService.currentOrder()?._id;
        if (!orderId) return;

        // Trocou de serviço antes da resposta chegar (A → B rápido): descarta a de A
        const isStale = () => this.orderMainService.currentOrder()?._id !== orderId;

        if (showLoading) this.loadingDocuments.set(true);
        this.documentController.getByOrderId(orderId).subscribe({
            next: documents => {
                if (isStale()) return;
                this.documents.set(documents);
                if (showLoading) this.loadingDocuments.set(false);
                this._syncProcessingDocumentsPolling();
            },
            error: error => {
                console.error(error);
                if (isStale()) return;
                if (showLoading) this._toastService.error(error.message);
                if (showLoading) this.loadingDocuments.set(false);
            },
        });
    }

    private _syncProcessingDocumentsPolling(): void {
        const hasProcessingDocument = this.documents().some(document =>
            document?.status === DocumentStatusEnum.PENDING ||
            document?.status === DocumentStatusEnum.CREATING
        );

        if (!hasProcessingDocument) {
            this._stopProcessingDocumentsPolling();
            return;
        }

        if (this._processingDocumentsTimer) return;

        this._processingDocumentsTimer = setInterval(() => this._getDocuments(false), 5000);
    }

    private _stopProcessingDocumentsPolling(): void {
        if (!this._processingDocumentsTimer) return;

        clearInterval(this._processingDocumentsTimer);
        this._processingDocumentsTimer = undefined;
    }

    /**
     * Chama o método para arquivar o serviço atual
     * Caso seja arquivado com sucesso, chama o método para remover o serviço da listagem de cache
     */
    public archiveOrder(): void {
        this.orderController.archive(this.orderMainService.currentOrder()?._id).subscribe({
            next: () => this.orderMainService.removeCurrentOrder(),
        });
    }

    /**
     * Chama o método para remover o serviço atual
     * Caso seja removido com sucesso, chama o método para remover o serviço da listagem de cache
     */
    public removeOrder(): void {
        // Usa o MESMO caminho do arquivar (`removeCurrentOrder`): tira o serviço da lista,
        // seleciona o próximo e revalida o feed (`refreshSilent`). O fluxo anterior chamava
        // `cleanCurrentOrder` + `getAllOrders`, que só atualizava a lista antiga de serviços —
        // o feed unificado (painel da esquerda) ficava com o serviço removido até dar F5.
        this.orderController.remove(this.orderMainService.currentOrder()?._id).subscribe({
            next: () => this.orderMainService.removeCurrentOrder(),
        });
    }

    /**
     * Copia o código da ordem de serviço. É o número que o cliente usa ao ligar,
     * então o caminho curto é copiar daqui em vez de digitar da tela.
     */
    public copyCode(): void {
        const code = this.orderMainService.currentOrder()?.code;
        if (!code) return;

        navigator.clipboard?.writeText(code);
        this._toastService.success({ message: this._translate.instant('order.common.codeCopied') });

        // O tique fica alguns segundos e volta ao ícone de copiar (sem isso, o
        // selo mentia: parecia que o código só é copiável uma vez).
        this.codeCopied.set(true);
        clearTimeout(this._codeCopiedTimeout);
        this._codeCopiedTimeout = setTimeout(() => this.codeCopied.set(false), 2000);
    }

    /**
     * Atualiza a descrição do serviço atual
     * @param description Descrição do serviço
     */
    public updateDescription(description: string): void {
        // Fecha o modal de edição de texto
        this.isEditingDescription.set(false);

        // Ativa o loading da mensagem de ação
        this.loadingActions.set(true);
        this.loadingActionsMessage.set(this._translate.instant('order.details.updatingDescription'));

        // Atualiza a descrição do serviço atual
        this.orderMainService.updateDescription(description).subscribe({
            next: () => {
                this.loadingActions.set(false);
                this.loadingActionsMessage.set(null);
            },
            error: () => {
                this.loadingActions.set(false);
                this.loadingActionsMessage.set(null);
            },
        });
    }

    /**
     * Atualiza o cliente do serviço atual
     * @param client Cliente
     */
    public selectClient(client: ClientEntity): void {
        this.isModalListClientsVisible.set(false);

        // Ativa o loading da mensagem de ação
        this.loadingActions.set(true);
        this.loadingActionsMessage.set(this._translate.instant('order.details.updatingClient'));

        this.orderMainService.updateClient(client).subscribe({
            next: () => {
                this.loadingActions.set(false);
                this.loadingActionsMessage.set(null);
            },
            error: () => {
                this.loadingActions.set(false);
                this.loadingActionsMessage.set(null);
            },
        });
    }

    /**
     * Cria um novo documento em branco
     */
    public createBlankDocument(): void {
        this.loadingCreateDocument.set(true);
        this._documentCreateController
            .insertBlankDocument(this.orderMainService.currentOrder()?._id)
            .subscribe({
                next: document => {
                    this.loadingCreateDocument.set(false);
                    this.documentController.navigateToDocument(document, true);
                },
                error: () => this.loadingCreateDocument.set(false),
            });
    }

    /**
     * Cria um novo contrato em branco (documento do tipo CONTRACT)
     */
    public createBlankContract(): void {
        this.loadingCreateDocument.set(true);
        this._documentCreateController
            .insertBlankDocument(
                this.orderMainService.currentOrder()?._id,
                DocumentTypeEnum.CONTRACT
            )
            .subscribe({
                next: document => {
                    this.loadingCreateDocument.set(false);
                    this.documentController.navigateToDocument(document, true);
                },
                error: () => this.loadingCreateDocument.set(false),
            });
    }

    /**
     * Cria um novo orçamento (documento do tipo QUOTE)
     */
    public createQuote(): void {
        this.loadingCreateDocument.set(true);
        this._documentCreateController
            .insertBlankDocument(
                this.orderMainService.currentOrder()?._id,
                DocumentTypeEnum.QUOTE
            )
            .subscribe({
                next: document => {
                    this.loadingCreateDocument.set(false);
                    this.documentController.navigateToDocument(document, true);
                },
                error: () => this.loadingCreateDocument.set(false),
            });
    }

    /**
     * Abre o modal de contratos (modelos internos)
     */
    public openInternalTemplatesModal(): void {
        this.isCreateDocumentOptionsVisible.set(false);
        this.isModalListInternalTemplatesVisible.set(true);
    }

    /**
     * Fecha o modal de contratos
     */
    public closeInternalTemplatesModal(): void {
        this.isModalListInternalTemplatesVisible.set(false);
    }

    /**
     * Seleciona um template para criar um novo documento
     * @param template Template
     */
    public selectTemplate(template: TemplateEntity): void {
        if (this.loadingCreateDocument()) return;

        this.loadingCreateDocument.set(true);
        const orderId = this.orderMainService.currentOrder()?._id;

        this._documentCreateController.insertByTemplate(orderId, template._id).subscribe({
            next: document => {
                this.loadingCreateDocument.set(false);
                this.isModalListTemplatesVisible.set(false);
                this.documentController.navigateToDocument(document, true);
            },
            error: () => this.loadingCreateDocument.set(false),
        });
    }

    /**
     * Seleciona um modelo interno (contrato) para criar um novo documento
     * @param internalTemplate Modelo interno
     */
    public selectInternalTemplate(internalTemplate: InternalTemplateEntity): void {
        if (this.loadingCreateDocument()) return;

        this.loadingCreateDocument.set(true);
        const orderId = this.orderMainService.currentOrder()?._id;

        this._documentCreateController
            .insertByInternalTemplate(orderId, internalTemplate._id)
            .subscribe({
                next: document => {
                    this.loadingCreateDocument.set(false);
                    this.closeInternalTemplatesModal();
                    this.documentController.navigateToDocument(document, true);
                },
                error: () => this.loadingCreateDocument.set(false),
            });
    }

    /**
     * Cria um resumo do serviço atual
     */
    public createResume(): void {
        // Clique duplo ou resumo ainda em geração: não pede outro
        if (this.loadingActions() || this._isResumePending()) return;

        // Ativa o loading da mensagem de ação
        this.loadingActions.set(true);
        this.loadingActionsMessage.set(this._translate.instant('order.details.creatingResume'));

        // Cria um resumo do serviço atual
        this.orderController.createResume(this.orderMainService.currentOrder()?._id).subscribe({
            next: () => {
                this.loadingActions.set(false);
                this.loadingActionsMessage.set(null);
                this._setResumeStatus(this.orderResumeStatusEnum.PENDING);
            },
            error: () => {
                this.loadingActions.set(false);
                this.loadingActionsMessage.set(null);
            },
        });
    }

    /** O resumo do serviço atual ainda está sendo gerado? */
    private _isResumePending(): boolean {
        return this.orderMainService.currentOrder()?.resumeStatus === this.orderResumeStatusEnum.PENDING;
    }

    /**
     * Troca o status do resumo num objeto novo (não muta o atual): assim o menu,
     * que é um `computed`, enxerga a mudança.
     */
    private _setResumeStatus(status: OrderResumeStatusEnum): void {
        const current = this.orderMainService.currentOrder();
        if (!current) return;

        this.orderMainService.currentOrder.set({ ...current, resumeStatus: status });
    }

    /** Troca a visão da lista e salva a preferência do usuário. */
    public setDocumentsView(view: DocumentsViewEnum): void {
        if (view === this.documentsView()) return;
        this._userController
            .updateDocumentsView(view)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({ error: () => {} });
    }

    /**
     * Indica se o documento foi gerado por IA (resumo do serviço ou geração via
     * chat). Usa o marcador persistente `generatedByIa`; para documentos antigos
     * sem o campo, ainda identifica os resumos por `type === RESUME`.
     */
    public isGeneratedByIa(document: DocumentEntity): boolean {
        return document?.generatedByIa === true || document?.type === DocumentTypeEnum.RESUME;
    }

    /**
     * % PERSISTIDO da conversão de um import em geração (espelha o feed-list — o
     * polling de 5s dos documentos em processamento já atualiza o valor sozinho).
     * `null` quando o documento não é um import em andamento.
     */
    public generationPercent(document: DocumentEntity): number | null {
        return importGenerationPercent(document);
    }

    /** Rótulo da etapa atual, com "(2/6)" quando a geração é dividida em partes. */
    public generationStageLabel(document: DocumentEntity): string {
        const progress = document?.importGenerationProgress;
        if (!progress) return this._translate.instant('order.feed.generating');

        const stage = this._translate.instant(`order.feed.importStages.${progress.stage}`);

        return progress.totalParts && progress.totalParts > 1
            ? `${stage} (${progress.part}/${progress.totalParts})`
            : stage;
    }

    /**
     * Clique direito no card: troca o menu do navegador pelo do documento, aberto
     * no ponto do clique. Card em geração não tem menu (nem recebe o evento).
     */
    public onDocumentContextMenu(event: MouseEvent, document: DocumentEntity): void {
        if (this._isProcessing(document)) return;
        event.preventDefault();

        const trigger = event.currentTarget as HTMLElement;

        // Shift+F10 / tecla de menu chegam sem coordenadas: ancora no próprio card
        const fromKeyboard = event.clientX === 0 && event.clientY === 0;

        this.documentMenuOrigin.set(
            fromKeyboard ? trigger : { x: event.clientX, y: event.clientY }
        );
        this.documentMenu.set({ document, trigger, source: fromKeyboard ? 'keyboard' : 'pointer' });
    }

    /** Botão ⋯ do card: abre (ou fecha, se já for ele) o menu do documento. */
    public onDocumentMoreClick(event: MouseEvent, document: DocumentEntity): void {
        // O card inteiro abre o documento: o clique no ⋯ não pode chegar nele
        event.stopPropagation();

        const trigger = event.currentTarget as HTMLElement;
        if (this.documentMenu()?.trigger === trigger) {
            this.closeDocumentMenu();
            return;
        }

        this.documentMenuOrigin.set(trigger);
        this.documentMenu.set({ document, trigger, source: 'button' });
    }

    public closeDocumentMenu(): void {
        this.documentMenu.set(null);
    }

    /**
     * Clique fora do menu fecha. O clique no próprio ⋯ que o abriu é ignorado aqui:
     * quem fecha é o toggle do botão — senão o menu fecharia e reabriria na hora.
     */
    public onDocumentMenuOutsideClick(event: MouseEvent): void {
        // O clique direito chega também como `auxclick`, depois do `contextmenu` que
        // acabou de abrir o menu noutro card. Quem decide no clique direito é o
        // `contextmenu`; sem isto, o menu recém-aberto fecharia na hora.
        if (event.type === 'auxclick' && event.button === 2) return;

        const menu = this.documentMenu();
        if (menu?.source === 'button' && menu.trigger.contains(event.target as Node)) return;

        this.closeDocumentMenu();
    }

    /** Esc fecha e devolve o foco para quem abriu (botão ⋯ ou card). */
    public onDocumentMenuKeydown(event: KeyboardEvent): void {
        if (event.key !== 'Escape') return;

        event.preventDefault();
        const trigger = this.documentMenu()?.trigger;
        this.closeDocumentMenu();
        trigger?.focus();
    }

    /**
     * Foca o primeiro item ao abrir, para o menu funcionar pelo teclado. Aberto
     * pelo mouse, o navegador não desenha o anel de foco.
     */
    public focusDocumentMenu(): void {
        // Os itens só existem depois do primeiro render do conteúdo do overlay
        afterNextRender(
            () => {
                const pane = this._documentMenuOverlay()?.overlayRef?.overlayElement;
                pane?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled)')?.focus({
                    preventScroll: true,
                });
            },
            { injector: this._injector }
        );
    }

    /** Despacha a ação escolhida no menu do documento. */
    public onDocumentMenuSelect(value: string): void {
        const document = this.documentMenu()?.document;
        this.closeDocumentMenu();
        if (!document) return;

        switch (value) {
            case 'open':
                this.documentController.navigateToDocument(document);
                break;
            case 'open-new-tab':
                this._openDocumentInNewTab(document);
                break;
            case 'rename':
                this.documentToRename.set(document);
                break;
            case 'duplicate':
                this._duplicateDocument(document);
                break;
            case 'move':
                this.documentToMove.set(document);
                break;
            case 'remove':
                this._removeDocument(document);
                break;
        }
    }

    /**
     * Salva o novo título. Otimista: o card já mostra o nome novo e volta ao
     * anterior se a API recusar.
     */
    public renameDocument(title: string): void {
        const document = this.documentToRename();
        this.documentToRename.set(null);

        // Título é uma linha só: quebras do textarea viram espaço
        const newTitle = title?.replace(/\s+/g, ' ').trim();
        if (!document || !newTitle || newTitle === document.title) return;

        this._patchDocument(document._id, { title: newTitle });
        this.documentController.updateTitle(document._id, newTitle).subscribe({
            next: () => this._feedMainService.refreshSilent(),
            error: () => this._patchDocument(document._id, { title: document.title }),
        });
    }

    /** Fecha o seletor de serviço e move o documento para o escolhido. */
    public moveDocument(order: OrderEntity): void {
        const document = this.documentToMove();
        this.documentToMove.set(null);
        if (!document || !order?._id) return;

        this._startDocumentAction('order.details.documentMenu.moving');
        this.documentController.move(document._id, order._id).subscribe({
            next: () => {
                this._stopDocumentAction();
                // Saiu deste serviço: some da lista (e a contagem do feed muda)
                this._removeDocumentFromList(document._id);
                this._feedMainService.refreshSilent();
            },
            error: () => this._stopDocumentAction(),
        });
    }

    /**
     * Duplica o documento (JSON + mídias) no mesmo serviço. Diferente do editor,
     * fica na lista: a cópia entra no topo, como o documento mais recente.
     */
    private _duplicateDocument(document: DocumentEntity): void {
        const orderId = this.orderMainService.currentOrder()?._id;

        this._startDocumentAction('order.details.documentMenu.duplicating');
        this.documentController.clone(document._id, document.orderId ?? orderId).subscribe({
            next: clonedDocument => {
                this._stopDocumentAction();

                // Trocou de serviço no meio da cópia: ela não é desta lista
                if (this.orderMainService.currentOrder()?._id === orderId) {
                    this._upsertDocument(clonedDocument, true);
                }
                this._feedMainService.refreshSilent();
            },
            error: () => this._stopDocumentAction(),
        });
    }

    /** Remove o documento (o controller pede a confirmação antes). */
    private _removeDocument(document: DocumentEntity): void {
        this.documentController.remove(document._id).subscribe({
            next: () => {
                this._removeDocumentFromList(document._id);
                this._feedMainService.refreshSilent();
            },
        });
    }

    /**
     * Abre o editor do documento em outra aba. A URL sai da estratégia de rotas
     * (hoje com `#`), então não depende de montar o caminho na mão.
     */
    private _openDocumentInNewTab(document: DocumentEntity): void {
        const route = this._router.serializeUrl(
            this._router.createUrlTree(['/document', document._id, 'edit'])
        );

        window.open(this._location.prepareExternalUrl(route), '_blank', 'noopener');
    }

    private _startDocumentAction(messageKey: string): void {
        this.loadingActions.set(true);
        this.loadingActionsMessage.set(this._translate.instant(messageKey));
    }

    private _stopDocumentAction(): void {
        this.loadingActions.set(false);
        this.loadingActionsMessage.set(null);
    }

    private _isProcessing(document: DocumentEntity): boolean {
        return (
            document?.status === DocumentStatusEnum.PENDING ||
            document?.status === DocumentStatusEnum.CREATING
        );
    }

    /** Aplica campos a um documento da lista, por `_id`. */
    private _patchDocument(documentId: string, changes: Partial<DocumentEntity>): void {
        this.documents.update(list =>
            list.map(d => (d._id === documentId ? { ...d, ...changes } : d))
        );
    }

    private _removeDocumentFromList(documentId: string): void {
        this.documents.update(list => list.filter(d => d._id !== documentId));
        this._syncProcessingDocumentsPolling();
    }

    /** Abre o modal de importação de arquivos (a partir do menu "Novo documento"). */
    public openImportDocuments(): void {
        this.isCreateDocumentOptionsVisible.set(false);
        this.isImportDocumentsVisible.set(true);
    }

    /**
     * O modal de import concluiu o `generate` e devolveu os placeholders PENDING —
     * adiciona os cards no feed na hora (o Pusher `document-import-pending` também
     * chega, mas o upsert por _id evita duplicar).
     */
    public onImportCreated(documents: DocumentEntity[]): void {
        for (const document of documents ?? []) this._upsertDocument(document, true);
        this._syncProcessingDocumentsPolling();
    }

    /**
     * Marca o documento como ERROR na lista. Sem isso o card do resumo ficaria
     * em "gerando" para sempre com a tela aberta.
     */
    private _markDocumentAsError(documentId: string): void {
        if (!documentId) return;

        this.documents.set(
            this.documents().map(d =>
                d._id === documentId ? { ...d, status: DocumentStatusEnum.ERROR } : d
            )
        );
    }

    /** Insere (prepend) ou atualiza um documento no feed por `_id`. */
    private _upsertDocument(document: DocumentEntity, prependIfNew: boolean): void {
        const list = this.documents();
        if (list.some(d => d._id === document._id)) {
            this.documents.set(
                list.map(d => (d._id === document._id ? { ...d, ...document } : d))
            );
        } else if (prependIfNew) {
            this.documents.set([document, ...list]);
        }
    }

    /**
     * Inicializa os pusher para os eventos de resumo
     * @param _destroyRef DestroyRef
     */
    private _initializePusher(_destroyRef: DestroyRef): void {
        const orderId = this.orderMainService.currentOrder()?._id;
        const channelName = `ORDER-${orderId}`;

        // Inicializa o pusher para o evento de resumo pendente
        this._pusherService
            .subscribe(channelName, 'order-resume-pending')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (document: DocumentEntity) => {
                    this._setResumeStatus(this.orderResumeStatusEnum.PENDING);

                    // Adiciona o documento na primeira posição da lista
                    this.documents.set([document, ...this.documents()]);
                    this._syncProcessingDocumentsPolling();
                },
            });

        // Inicializa o pusher para o evento de resumo criado
        this._pusherService
            .subscribe(channelName, 'order-resume-created')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (resp: DocumentEntity) => {
                    this._setResumeStatus(this.orderResumeStatusEnum.COMPLETED);

                    // Atualiza o documento na lista
                    this.documents.set(
                        this.documents().map(document =>
                            document._id === resp._id
                                ? { ...document, ...resp }
                                : document
                        )
                    );
                    this._syncProcessingDocumentsPolling();

                    // Notifica o usuário que o resumo foi criado
                    this._toastService.success({
                        message: this._translate.instant('order.details.resumeCreatedSuccess'),
                    });
                },
            });

        // Resumo falhou em definitivo (o worker esgotou as tentativas). O payload
        // é `{ orderId, documentResumeId }`, NÃO um documento, por isso o status
        // do card é marcado localmente.
        this._pusherService
            .subscribe(channelName, 'order-resume-error')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (resp: { orderId: string; documentResumeId: string }) => {
                    this._setResumeStatus(this.orderResumeStatusEnum.ERROR);

                    this._markDocumentAsError(resp?.documentResumeId);
                    this._syncProcessingDocumentsPolling();

                    this._toastService.error({
                        message: this._translate.instant('order.details.resumeError'),
                    });
                },
            });

        // Import de arquivos — pendente: adiciona o(s) card(s) em conversão.
        this._pusherService
            .subscribe(channelName, 'document-import-pending')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (document: DocumentEntity) => {
                    this._upsertDocument(document, true);
                    this._syncProcessingDocumentsPolling();
                },
            });

        // Import — criado: troca o card de "convertendo" por pronto + toast.
        this._pusherService
            .subscribe(channelName, 'document-import-created')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (resp: DocumentEntity) => {
                    this._upsertDocument(resp, false);
                    this._syncProcessingDocumentsPolling();
                    this._toastService.success({
                        message: this._translate.instant('order.documentImport.createdSuccess'),
                    });
                },
            });

        // Import — erro: marca o card com ERROR + toast.
        this._pusherService
            .subscribe(channelName, 'document-import-error')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (resp: DocumentEntity) => {
                    this._upsertDocument(resp, false);
                    this._syncProcessingDocumentsPolling();
                    this._toastService.error({
                        message: this._translate.instant('order.documentImport.errorGenerating'),
                    });
                },
            });

        // Chat de IA — placeholder criado: entra o card em geração.
        this._pusherService
            .subscribe(channelName, 'ia-chat-document-pending')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (document: DocumentEntity) => {
                    this._upsertDocument(document, true);
                    this._syncProcessingDocumentsPolling();
                },
            });

        // Chat de IA — pronto: troca o card de "gerando" por pronto + toast.
        this._pusherService
            .subscribe(channelName, 'ia-chat-document-created')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (resp: DocumentEntity) => {
                    this._upsertDocument(resp, false);
                    this._syncProcessingDocumentsPolling();
                    this._toastService.success({
                        message: this._translate.instant('iaChat.documentCreatedSuccess'),
                    });
                },
            });

        // Chat de IA — erro: marca o card com ERROR + toast.
        this._pusherService
            .subscribe(channelName, 'ia-chat-document-error')
            .pipe(takeUntilDestroyed(_destroyRef))
            .subscribe({
                next: (resp: DocumentEntity) => {
                    this._upsertDocument(resp, false);
                    this._syncProcessingDocumentsPolling();
                    this._toastService.error({
                        message: this._translate.instant('iaChat.documentErrorGenerating'),
                    });
                },
            });
    }
}
